// src/prototype/components/MilestoneRow.tsx
// One milestone on the Initiative Command Center: status icon, title + phase tag,
// owner/due line, progress bar while in flight, and a blocker line when blocked.
//
// MAINTAINED, not frozen. When onSetStatus is supplied the row grows an "Update status"
// control (revealed on hover/focus) opening a small popover: Not Started · In Progress
// (25/50/75) · Complete. Everything the row shows — icon, colour, percentage, bar, status
// label — derives from the status it is handed, so the write and the render cannot disagree.
//
// The control stays invisible until hover/focus on purpose: a maintained record should look
// like a record, not a form.
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Zap, Clock, AlertTriangle, Pencil } from 'lucide-react';
import { MILESTONE_STATUS_COLORS, type MilestoneStatus } from '../theme/decisionTokens';
import type { ExecMilestone } from '../data/executionInitiatives';
import { IN_PROGRESS_PICKS } from '../hooks/useExecutionRecordState';

const STATUS_ICON: Record<MilestoneStatus, React.ElementType> = {
  'Complete': CheckCircle2,
  'In Progress': Zap,
  'Not Started': Clock,
};

export interface MilestoneRowProps {
  milestone: ExecMilestone;
  /** Supply to make the row editable. Omitted → renders exactly as it did before. */
  onSetStatus?: (status: MilestoneStatus, pick?: number) => void;
  /** Shown when this row carries a presenter edit, e.g. "Updated just now · Initiative Owner". */
  updatedLabel?: string;
}

export const MilestoneRow: React.FC<MilestoneRowProps> = ({ milestone, onSetStatus, updatedLabel }) => {
  const color = MILESTONE_STATUS_COLORS[milestone.status] ?? MILESTONE_STATUS_COLORS['Not Started'];
  const Icon = STATUS_ICON[milestone.status] ?? Clock;
  const inFlight = milestone.pct > 0 && milestone.pct < 100;

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape — a popover that traps the presenter is worse than none.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (status: MilestoneStatus, pick?: number) => {
    onSetStatus?.(status, pick);
    setOpen(false);
  };

  return (
    <div className="group relative flex items-start gap-3 bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] px-3.5 py-3">
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-[#18365E]">{milestone.title}</span>
          <span className="text-[9px] font-bold uppercase tracking-wide text-[#717182] bg-white border border-[#e5e7eb] px-1.5 py-0.5 rounded">
            {milestone.phase}
          </span>
        </div>
        <p className="text-[11px] text-[#717182] mt-0.5">
          {milestone.owner} · due {milestone.due}
          {milestone.status === 'In Progress' && ` · ${milestone.pct}% complete`}
        </p>
        {inFlight && (
          <div className="h-1.5 bg-white rounded-full mt-1.5 border border-[#e5e7eb] overflow-hidden">
            <div
              className="h-full bg-[#2563eb] rounded-full transition-all duration-300"
              style={{ width: `${milestone.pct}%` }}
              data-testid="milestone-bar"
            />
          </div>
        )}
        {milestone.blocker && (
          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid="milestone-blocker">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {milestone.blocker}
          </p>
        )}
        {updatedLabel && (
          <p className="text-[10px] text-[#717182] mt-1 italic" data-testid="milestone-updated">
            {updatedLabel}
          </p>
        )}
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0" ref={wrapRef}>
        <span className="text-[10px] font-semibold" style={{ color }}>
          {milestone.status}
        </span>

        {onSetStatus && (
          <>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={`Update status — ${milestone.title}`}
              className={`text-[10px] font-semibold text-[#18365E] hover:text-[#FF6E4C] inline-flex items-center gap-1 transition-opacity ${
                open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
              }`}
            >
              <Pencil className="w-3 h-3" />
              Update status
            </button>

            {open && (
              <div
                role="menu"
                className="absolute right-0 top-8 z-20 w-52 bg-white border border-[#e5e7eb] rounded-lg shadow-lg p-1.5"
              >
                <p className="text-[9px] font-semibold uppercase tracking-wide text-[#717182] px-2 pt-1 pb-1.5">
                  Update status
                </p>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => choose('Not Started')}
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-[#F5F7FA] text-[#4b5563]"
                >
                  Not Started
                </button>

                <div className="px-2 py-1.5">
                  <p className="text-xs text-[#4b5563] mb-1">In Progress</p>
                  <div className="flex gap-1">
                    {IN_PROGRESS_PICKS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        role="menuitem"
                        onClick={() => choose('In Progress', p)}
                        className="flex-1 text-[11px] font-semibold px-1.5 py-1 rounded border border-[#e5e7eb] hover:border-[#18365E] hover:bg-[#F5F7FA] text-[#18365E]"
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => choose('Complete')}
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-[#F5F7FA] text-[#4b5563]"
                >
                  Complete
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MilestoneRow;
